import type { FileSystemDirectoryHandle, FileSystemFileHandle } from '../types/filesystem';
import { storageService, type AlbumArtFile } from '../services/storageService';

// Supported image file extensions for album art
const SUPPORTED_IMAGE_EXTENSIONS = [
    '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff', '.tif'
];

// Album art filename patterns to detect
const ALBUM_ART_PATTERNS = [
    /^albumart/i,
    /^album[_\-\s]*art/i,
    /^cover/i,
    /^folder/i,
    /^front/i
];

/**
 * Check if a filename matches album art patterns
 */
export function isAlbumArtFile(filename: string): boolean {
    const lowerName = filename.toLowerCase();

    // Check if it has a supported image extension
    const hasImageExtension = SUPPORTED_IMAGE_EXTENSIONS.some(ext =>
        lowerName.endsWith(ext)
    );

    if (!hasImageExtension) {
        return false;
    }

    // Check if filename matches any album art pattern
    return ALBUM_ART_PATTERNS.some(pattern => pattern.test(filename));
}

/**
 * Generate a unique ID for an album art file based on its path and content
 */
function generateAlbumArtId(path: string, size: number, lastModified: number): string {
    const hashInput = `${path}:${size}:${lastModified}`;
    // Simple hash function - in production, consider using a proper hash library
    let hash = 0;
    for (let i = 0; i < hashInput.length; i++) {
        const char = hashInput.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    return `albumart_${Math.abs(hash).toString(36)}`;
}

/**
 * Get MIME type from file extension
 */
function getMimeTypeFromExtension(filename: string): string {
    const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));

    switch (extension) {
        case '.jpg':
        case '.jpeg':
            return 'image/jpeg';
        case '.png':
            return 'image/png';
        case '.gif':
            return 'image/gif';
        case '.bmp':
            return 'image/bmp';
        case '.webp':
            return 'image/webp';
        case '.tiff':
        case '.tif':
            return 'image/tiff';
        default:
            return 'image/jpeg'; // Default fallback
    }
}

/**
 * Extract directory path from a full file path
 */
function getDirectoryPath(filePath: string): string {
    const lastSlash = filePath.lastIndexOf('/');
    return lastSlash > 0 ? filePath.substring(0, lastSlash) : '';
}

/**
 * Process an album art file and store it in IndexedDB
 */
export async function processAlbumArtFile(
    fileHandle: FileSystemFileHandle,
    fullPath: string
): Promise<AlbumArtFile | null> {
    try {
        const file = await fileHandle.getFile();
        const directoryPath = getDirectoryPath(fullPath);

        // Generate unique ID
        const id = generateAlbumArtId(fullPath, file.size, file.lastModified);

        // Check if we already have this file in storage (with error handling)
        let existingArt: AlbumArtFile | null = null;
        try {
            existingArt = await storageService.loadAlbumArt(id);
            if (existingArt && existingArt.lastModified === file.lastModified) {
                // File hasn't changed, return existing
                return existingArt;
            }
        } catch (error) {
            // Ignore errors when checking for existing art (database might not be upgraded yet)
            console.log(`Could not check for existing album art ${id}, proceeding with fresh import`);
        }

        // Read file data
        const arrayBuffer = await file.arrayBuffer();

        const albumArt: AlbumArtFile = {
            id,
            filename: file.name,
            path: fullPath,
            directoryPath,
            mimeType: getMimeTypeFromExtension(file.name),
            size: file.size,
            lastModified: file.lastModified,
            data: arrayBuffer,
            fileHandle
        };

        // Store in IndexedDB (with error handling)
        try {
            await storageService.saveAlbumArt(albumArt);
        } catch (error) {
            console.warn(`Failed to save album art ${fullPath} to storage:`, error);
            // Continue anyway, we still have the data in memory
        }

        return albumArt;
    } catch (error) {
        console.warn(`Failed to process album art file ${fullPath}:`, error);
        return null;
    }
}

/**
 * Scan a directory for album art files
 */
export async function scanDirectoryForAlbumArt(
    directoryHandle: FileSystemDirectoryHandle,
    currentPath = ''
): Promise<AlbumArtFile[]> {
    const albumArtFiles: AlbumArtFile[] = [];

    try {
        for await (const [name, handle] of directoryHandle.entries()) {
            const fullPath = currentPath ? `${currentPath}/${name}` : name;

            if (handle.kind === 'file' && isAlbumArtFile(name)) {
                const albumArt = await processAlbumArtFile(handle as FileSystemFileHandle, fullPath);
                if (albumArt) {
                    albumArtFiles.push(albumArt);
                }
            } else if (handle.kind === 'directory') {
                // Recursively scan subdirectories
                const subDirAlbumArt = await scanDirectoryForAlbumArt(
                    handle as FileSystemDirectoryHandle,
                    fullPath
                );
                albumArtFiles.push(...subDirAlbumArt);
            }
        }
    } catch (error) {
        console.warn(`Error scanning directory ${currentPath} for album art:`, error);
    }

    return albumArtFiles;
}

/**
 * Get album art files for a specific directory
 */
export async function getAlbumArtForDirectory(directoryPath: string): Promise<AlbumArtFile[]> {
    try {
        return await storageService.loadAlbumArtByDirectory(directoryPath);
    } catch (error) {
        console.warn(`Failed to load album art for directory ${directoryPath}:`, error);
        return [];
    }
}

/**
 * Create a blob URL for displaying album art
 */
export function createAlbumArtUrl(albumArt: AlbumArtFile): string {
    const blob = new Blob([albumArt.data], { type: albumArt.mimeType });
    return URL.createObjectURL(blob);
}

/**
 * Clean up blob URLs when no longer needed
 */
export function revokeAlbumArtUrl(url: string): void {
    try {
        URL.revokeObjectURL(url);
    } catch (error) {
        console.warn('Error revoking album art URL:', error);
    }
}

/**
 * Associate album art with music files in the same directory
 * Returns a map of music file paths to their associated album art
 */
export function associateAlbumArtWithMusicFiles(
    musicFilePaths: string[],
    albumArtFiles: AlbumArtFile[]
): Map<string, AlbumArtFile> {
    const associations = new Map<string, AlbumArtFile>();

    // Create a map of directory paths to album art files for quick lookup
    const albumArtByDirectory = new Map<string, AlbumArtFile[]>();

    albumArtFiles.forEach(art => {
        if (!albumArtByDirectory.has(art.directoryPath)) {
            albumArtByDirectory.set(art.directoryPath, []);
        }
        albumArtByDirectory.get(art.directoryPath)!.push(art);
    });

    // Associate each music file with album art from its directory
    musicFilePaths.forEach(musicPath => {
        const musicDirPath = getDirectoryPath(musicPath);
        const dirAlbumArt = albumArtByDirectory.get(musicDirPath);

        if (dirAlbumArt && dirAlbumArt.length > 0) {
            // Choose the "best" album art file:
            // 1. Prefer files with "Large" in name
            // 2. Then prefer files with "cover" or "front" in name
            // 3. Then prefer largest file
            let bestArt = dirAlbumArt[0];

            for (const art of dirAlbumArt) {
                const artName = art.filename.toLowerCase();
                const bestName = bestArt.filename.toLowerCase();

                // Prefer "Large" files
                if (artName.includes('large') && !bestName.includes('large')) {
                    bestArt = art;
                } else if (!artName.includes('large') && bestName.includes('large')) {
                    continue;
                } else if (artName.includes('cover') || artName.includes('front')) {
                    if (!bestName.includes('cover') && !bestName.includes('front')) {
                        bestArt = art;
                    }
                } else if (art.size > bestArt.size) {
                    // Prefer larger files
                    bestArt = art;
                }
            }

            associations.set(musicPath, bestArt);
        }
    });

    return associations;
}